import clsx from "clsx";
import type { ReactNode } from "react";
import { useRef } from "react";
import { Section } from "@/components/general/section";
import styles from "./MobileBottomSheet.module.css";

type MobileBottomSheetProps = {
  isDisabled: boolean;
  canOpen?: boolean;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  children: ReactNode;
  pulsating?: boolean;
};

type DragState = {
  startY: number;
  startTranslate: number;
  closedTranslate: number;
  isDragging: boolean;
  lastTranslate: number;
};

const DRAG_START_THRESHOLD_PX = 5;
const OPEN_THRESHOLD_RATIO = 0.5;
const SETTLE_FALLBACK_MS = 350;

export const MobileBottomSheet = ({
  isOpen,
  onOpenChange,
  children,
  pulsating = false,
  isDisabled = false,
  canOpen = true,
}: MobileBottomSheetProps) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const suppressClickRef = useRef(false);
  const blockInteraction = isDisabled || (!isOpen && !canOpen);

  const handleOverlayClick = () => {
    if (blockInteraction) return;
    onOpenChange(!isOpen);
  };

  const handleToggleClick = () => {
    if (blockInteraction) return;
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    onOpenChange(!isOpen);
  };

  const getClosedTranslate = (section: HTMLElement) =>
    Math.max(
      0,
      section.getBoundingClientRect().height - window.innerHeight * 0.05
    );

  const applyDragProgress = (
    section: HTMLElement,
    overlay: HTMLElement,
    translate: number,
    closedTranslate: number
  ) => {
    section.style.transform = `translateY(${translate}px)`;
    const progress = 1 - translate / Math.max(closedTranslate, 1);
    overlay.style.opacity = String(Math.min(1, Math.max(0, progress)));
  };

  const settleTransform = (
    section: HTMLElement,
    overlay: HTMLElement,
    open: boolean,
    closedTranslate: number
  ) => {
    section.style.transition = "";
    overlay.style.transition = "";
    section.style.transform = open
      ? "translateY(0)"
      : `translateY(${closedTranslate}px)`;
    overlay.style.opacity = open ? "1" : "0";

    let settled = false;
    const cleanup = () => {
      if (settled) return;
      settled = true;
      section.style.transform = "";
      section.style.transition = "";
      overlay.style.opacity = "";
      overlay.style.transition = "";
    };
    section.addEventListener("transitionend", cleanup, { once: true });
    setTimeout(() => {
      section.removeEventListener("transitionend", cleanup);
      cleanup();
    }, SETTLE_FALLBACK_MS);
  };

  const handleDragStart = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (blockInteraction) return;
    const section = sectionRef.current;
    const overlay = overlayRef.current;
    if (!section || !overlay) return;

    const closedTranslate = getClosedTranslate(section);
    const startTranslate = isOpen ? 0 : closedTranslate;
    dragRef.current = {
      startY: e.clientY,
      startTranslate,
      closedTranslate,
      isDragging: false,
      lastTranslate: startTranslate,
    };
    section.style.transition = "none";
    overlay.style.transition = "none";
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handleDragMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (blockInteraction) return;
    const drag = dragRef.current;
    const section = sectionRef.current;
    const overlay = overlayRef.current;
    if (!drag || !section || !overlay) return;

    const delta = e.clientY - drag.startY;
    if (!drag.isDragging) {
      if (Math.abs(delta) < DRAG_START_THRESHOLD_PX) return;
      drag.isDragging = true;
      suppressClickRef.current = true;
    }

    const nextTranslate = Math.min(
      drag.closedTranslate,
      Math.max(0, drag.startTranslate + delta)
    );
    drag.lastTranslate = nextTranslate;
    applyDragProgress(section, overlay, nextTranslate, drag.closedTranslate);
  };

  const handleDragEnd = (e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    const section = sectionRef.current;
    const overlay = overlayRef.current;
    dragRef.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
    if (!section || !overlay) return;

    if (!drag?.isDragging) {
      section.style.transition = "";
      overlay.style.transition = "";
      return;
    }

    const delta = e.clientY - drag.startY;
    const finalTranslate = Math.min(
      drag.closedTranslate,
      Math.max(0, drag.startTranslate + delta)
    );
    const shouldOpen =
      finalTranslate < drag.closedTranslate * OPEN_THRESHOLD_RATIO;

    settleTransform(section, overlay, shouldOpen, drag.closedTranslate);
    onOpenChange(shouldOpen);
  };

  const handleDragCancel = (e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    const section = sectionRef.current;
    const overlay = overlayRef.current;
    dragRef.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
    if (!section || !overlay) return;

    if (drag?.isDragging) {
      const shouldOpen =
        drag.lastTranslate < drag.closedTranslate * OPEN_THRESHOLD_RATIO;
      settleTransform(section, overlay, shouldOpen, drag.closedTranslate);
      onOpenChange(shouldOpen);
    } else {
      section.style.transition = "";
      overlay.style.transition = "";
    }
  };

  return (
    <>
      <div
        ref={overlayRef}
        onClick={handleOverlayClick}
        className={clsx(styles.overlay, isOpen && styles.open)}
      />
      <Section
        ref={sectionRef}
        className={clsx(
          styles.fixedSection,
          isOpen && styles.open,
          pulsating && styles.pulsating
        )}
      >
        <button
          type="button"
          className={clsx(
            styles.dragButton,
            blockInteraction && styles.disabled
          )}
          disabled={blockInteraction}
          aria-expanded={isOpen}
          aria-label={
            isOpen ? "Collapse payment details" : "Expand payment details"
          }
          onClick={handleToggleClick}
          onPointerDown={handleDragStart}
          onPointerMove={handleDragMove}
          onPointerUp={handleDragEnd}
          onPointerCancel={handleDragCancel}
        >
          <span aria-hidden="true" className={styles.dragIcon} />
        </button>
        {children}
      </Section>
    </>
  );
};
